import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Loader2 } from "lucide-react";

// Animated background particles
function FloatingParticles() {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {/* Gradient orbs */}
      <div className="absolute top-1/4 -left-20 w-96 h-96 bg-[#FF4500]/10 rounded-full blur-[120px] animate-float-slow" />
      <div className="absolute bottom-1/4 -right-20 w-80 h-80 bg-[#FF4500]/5 rounded-full blur-[100px] animate-float-slow" style={{animationDelay: '-5s'}} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#FF4500]/[0.02] rounded-full blur-[150px]" />
      
      {/* Geometric shapes */}
      <div className="absolute top-[15%] left-[10%] w-px h-32 bg-gradient-to-b from-transparent via-white/10 to-transparent animate-pulse-slow" />
      <div className="absolute top-[25%] right-[15%] w-px h-24 bg-gradient-to-b from-transparent via-[#FF4500]/20 to-transparent animate-pulse-slow" style={{animationDelay: '-2s'}} />
      <div className="absolute bottom-[20%] left-[20%] w-px h-20 bg-gradient-to-b from-transparent via-white/5 to-transparent animate-pulse-slow" style={{animationDelay: '-4s'}} />
      
      {/* Floating squares */}
      <div className="absolute top-[20%] right-[25%] w-4 h-4 border border-white/[0.05] rotate-45 animate-float" />
      <div className="absolute bottom-[30%] left-[15%] w-6 h-6 border border-[#FF4500]/10 rotate-12 animate-float" style={{animationDelay: '-3s'}} />
      <div className="absolute top-[60%] right-[10%] w-3 h-3 border border-white/[0.03] -rotate-12 animate-float" style={{animationDelay: '-1.5s'}} />
      
      {/* Dot grid pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
        backgroundSize: '30px 30px'
      }} />
    </div>
  );
}

// Animated logo component
function AnimatedLogo() {
  const [show, setShow] = useState(false);
  
  useEffect(() => {
    setTimeout(() => setShow(true), 100);
  }, []);
  
  return (
    <div className="text-center mb-10 sm:mb-12">
      {/* Main logo with reveal animation */}
      <div className={`relative inline-block transition-all duration-1000 ease-out ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <h1 
          className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white relative z-10" 
          style={{fontFamily: "'Playfair Display', serif"}}
        >
          TAT<span className="text-[#FF4500] animate-pulse-glow">.</span>
        </h1>
        
        {/* Glow effect behind logo */}
        <div className="absolute inset-0 blur-2xl bg-[#FF4500]/20 scale-150 animate-pulse-slow opacity-50" />
      </div>
      
      {/* Tagline with stagger animation */}
      <div className={`mt-3 overflow-hidden transition-all duration-700 delay-300 ${show ? 'opacity-100' : 'opacity-0'}`}>
        <p className="text-white/25 text-[10px] sm:text-xs tracking-[0.4em] uppercase animate-slide-in-up" style={{animationDelay: '0.5s'}}>
          Atelier
        </p>
      </div>
      
      {/* Animated line */}
      <div className={`mx-auto mt-5 h-px bg-gradient-to-r from-transparent via-[#FF4500]/30 to-transparent transition-all duration-1000 delay-500 ${show ? 'w-20 opacity-100' : 'w-0 opacity-0'}`} />
      
      {/* Subtitle */}
      <p className={`mt-4 text-white/15 text-[8px] sm:text-[9px] tracking-[0.3em] uppercase transition-all duration-700 delay-700 ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
        The Architecture Tale
      </p>
    </div>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [formVisible, setFormVisible] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    // Check if remember me was previously enabled
    return localStorage.getItem("rememberMe") === "true";
  });
  
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const initSystem = async () => {
      try {
        setInitializing(true);
        await apiClient.post("/setup/init");
      } catch (error) {
        console.log("System initialization:", error.response?.data?.message || "Ready");
      } finally {
        setInitializing(false);
      }
    };
    initSystem();
    
    // Show form with delay
    setTimeout(() => setFormVisible(true), 800);
    
    // Load saved email if remember me was enabled
    const savedEmail = localStorage.getItem("savedEmail");
    if (savedEmail && localStorage.getItem("rememberMe") === "true") {
      setEmail(savedEmail);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Save or clear remember me preference
    if (rememberMe) {
      localStorage.setItem("rememberMe", "true");
      localStorage.setItem("savedEmail", email);
    } else {
      localStorage.removeItem("rememberMe");
      localStorage.removeItem("savedEmail");
    }
    
    const result = await login(email, password, rememberMe);
    
    if (result.success) {
      navigate("/dashboard");
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] p-4 sm:p-6 overflow-hidden pt-safe pb-safe" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))', paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }} data-testid="login-page">
      {/* Animated background */}
      <FloatingParticles />
      
      {/* Login Card */}
      <div className="w-full max-w-sm relative z-10" data-testid="login-card">
        {/* Animated Logo */}
        <AnimatedLogo />
        
        {/* Welcome Text */}
        <div className={`text-center mb-8 sm:mb-10 transition-all duration-700 ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <h2 className="text-xl sm:text-2xl font-light text-white/90 mb-2" style={{fontFamily: "'Playfair Display', serif"}}>
            Welcome Back
          </h2>
          <p className="text-white/25 text-[10px] sm:text-xs tracking-wider">Sign in to your workspace</p>
        </div>

        {/* Form with staggered animations */}
        <form onSubmit={handleSubmit} className={`space-y-5 sm:space-y-6 transition-all duration-700 delay-100 ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          {/* Email Field */}
          <div className="space-y-2 group">
            <Label 
              htmlFor="email" 
              className={`text-[10px] sm:text-xs tracking-[0.2em] uppercase transition-all duration-300 ${
                focusedField === 'email' ? 'text-[#FF4500]' : 'text-white/30'
              }`}
            >
              Email
            </Label>
            <div className="relative">
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField(null)}
                placeholder="your@email.com"
                className="h-12 sm:h-14 bg-white/[0.02] border-white/[0.08] text-white text-sm placeholder:text-white/15 focus:border-[#FF4500]/50 focus:bg-white/[0.04] focus:ring-0 rounded-xl transition-all duration-500 pl-4"
                required
                data-testid="email-input"
              />
              {/* Animated border glow */}
              <div className={`absolute inset-0 rounded-xl pointer-events-none transition-opacity duration-500 ${
                focusedField === 'email' ? 'opacity-100' : 'opacity-0'
              }`} style={{
                boxShadow: '0 0 20px rgba(255, 69, 0, 0.15), inset 0 0 20px rgba(255, 69, 0, 0.05)'
              }} />
            </div>
          </div>

          {/* Password Field */}
          <div className="space-y-2 group">
            <Label 
              htmlFor="password" 
              className={`text-[10px] sm:text-xs tracking-[0.2em] uppercase transition-all duration-300 ${
                focusedField === 'password' ? 'text-[#FF4500]' : 'text-white/30'
              }`}
            >
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField(null)}
                placeholder="••••••••"
                className="h-12 sm:h-14 bg-white/[0.02] border-white/[0.08] text-white text-sm placeholder:text-white/15 focus:border-[#FF4500]/50 focus:bg-white/[0.04] focus:ring-0 rounded-xl transition-all duration-500 pl-4 pr-12"
                required
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/20 hover:text-[#FF4500] transition-all duration-300 hover:scale-110"
                data-testid="toggle-password"
              >
                {showPassword ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5" />}
              </button>
              {/* Animated border glow */}
              <div className={`absolute inset-0 rounded-xl pointer-events-none transition-opacity duration-500 ${
                focusedField === 'password' ? 'opacity-100' : 'opacity-0'
              }`} style={{
                boxShadow: '0 0 20px rgba(255, 69, 0, 0.15), inset 0 0 20px rgba(255, 69, 0, 0.05)'
              }} />
            </div>
          </div>

          {/* Keep me signed in */}
          <div className="flex items-center gap-3 py-1">
            <button
              type="button"
              onClick={() => setRememberMe(!rememberMe)}
              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-300 ${
                rememberMe 
                  ? 'bg-[#FF4500] border-[#FF4500]' 
                  : 'bg-transparent border-white/20 hover:border-white/40'
              }`}
              data-testid="remember-me-checkbox"
            >
              {rememberMe && (
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
            <span 
              className="text-white/40 text-xs tracking-wide cursor-pointer hover:text-white/60 transition-colors"
              onClick={() => setRememberMe(!rememberMe)}
            >
              Keep me signed in
            </span>
          </div>

          {/* Submit Button */}
          <div className="pt-2">
            <Button
              type="submit"
              disabled={loading || initializing}
              className="w-full h-12 sm:h-14 bg-gradient-to-r from-[#FF4500] to-[#FF6B35] hover:from-[#FF5722] hover:to-[#FF7043] text-white text-xs sm:text-sm font-medium tracking-[0.15em] uppercase rounded-xl transition-all duration-500 hover:shadow-xl hover:shadow-[#FF4500]/25 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 relative overflow-hidden group"
              data-testid="login-btn"
            >
              {/* Button shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              
              {loading ? (
                <span className="flex items-center justify-center relative z-10">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </span>
              ) : initializing ? (
                <span className="flex items-center justify-center relative z-10">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Preparing...
                </span>
              ) : (
                <span className="relative z-10">Sign In</span>
              )}
            </Button>
          </div>
        </form>

        {/* Elegant Divider */}
        <div className={`flex items-center gap-4 my-8 sm:my-10 transition-all duration-700 delay-300 ${formVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent to-white/[0.05]" />
          <div className="w-1.5 h-1.5 rounded-full bg-[#FF4500]/30" />
          <div className="flex-1 h-px bg-gradient-to-l from-transparent to-white/[0.05]" />
        </div>

        {/* Architecture Quote */}
        <div className={`text-center transition-all duration-700 delay-500 ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <p className="text-white/20 text-[9px] sm:text-[10px] italic leading-relaxed max-w-xs mx-auto">
            "Architecture is the thoughtful making of space"
          </p>
          <p className="text-[#FF4500]/40 text-[8px] sm:text-[9px] mt-2 tracking-wider uppercase">
            — Louis Kahn
          </p>
        </div>
        
        {/* Footer */}
        <div className={`text-center mt-10 sm:mt-12 transition-all duration-700 delay-700 ${formVisible ? 'opacity-100' : 'opacity-0'}`}>
          <p className="text-white/10 text-[8px] sm:text-[9px] tracking-[0.2em] uppercase">
            TAT Atelier © 2026
          </p>
          <p className="text-white/5 text-[7px] sm:text-[8px] mt-1 tracking-wider">
            Architecture & Design
          </p>
        </div>
      </div>
    </div>
  );
}
